'use client'

import dynamic from 'next/dynamic'
import { PropsWithChildren, useCallback, useEffect, useRef, useState } from 'react'
import { SwiperRef } from 'swiper/react'
import { Swiper as SwiperType } from 'swiper/types'
import { AtButton, NavArrowLeft, NavArrowRight } from '../../atoms'
import { MlCustomCardProps, MlSpaceCardProps, MlSpaceCardSkeleton, MlSpaceCardSkeletonVariant } from '../../molecules'
import { AtButtonDimensions, AtButtonTypes } from '../../types'
import { MlBuildingCardProps } from '../../molecules/ml-building-card'
import { cn } from '../../utils'
import { CarouselType } from './types'

// Re-export for external usage
export { CarouselType }

const SwiperEngine = dynamic(() => import('./SwiperEngine').then((mod) => mod.SwiperEngine), {
  ssr: false,
})

type OrCardCarouselDataProps =
  | { type: CarouselType.Product; cards?: MlSpaceCardProps[]; size?: 'small' | 'medium' }
  | { type: CarouselType.Building; cards?: MlBuildingCardProps[]; size?: undefined }
  | { type: CarouselType.CustomCard; cards?: MlCustomCardProps[]; size?: undefined }
  | { type?: undefined; cards?: MlSpaceCardProps[]; size?: 'small' | 'medium' }

export type OrCardCarouselProps = {
  title?: string
  loading?: boolean
  loadingPlaceholders?: number
  buttonPositioning?: string
  slideClassName?: string
  embedded?: boolean
  className?: string
  loadMore?: () => void
  productsSize?: number
} & OrCardCarouselDataProps

export const OrCardCarousel = ({
  title,
  type,
  cards,
  loading = false,
  loadingPlaceholders = 4,
  size = 'medium',
  buttonPositioning,
  slideClassName = !type || type === CarouselType.Product ? 'pb-4 ' : 'pb-4 self-stretch !h-auto',
  embedded = false,
  className,
  loadMore,
  productsSize,
}: OrCardCarouselProps) => {
  const swiperRef = useRef<SwiperRef | null>(null)
  const [beginning, setBeginning] = useState(true)
  const [end, setEnd] = useState(true)
  const [isMounted, setIsMounted] = useState(false)

  const updateLimits = useCallback((swiper: SwiperType) => {
    setBeginning(swiper.isBeginning)
    setEnd(swiper.isEnd)
  }, [])

  const onSwiperReady = useCallback(
    (swiper: SwiperType) => {
      updateLimits(swiper)
    },
    [updateLimits]
  )

  useEffect(() => {
    setIsMounted(true)
  }, [])

  const onReachEnd = useCallback(async () => {
    setEnd(true)
    if (!type || type === CarouselType.Product) {
      if (!productsSize || !cards || !loadMore) return
      if (cards.length >= productsSize || cards.length === 0) return
      loadMore && (await loadMore())
      setEnd(false)
    }
  }, [type, productsSize, cards, loadMore])

  return (
    <Container
      title={title}
      type={type}
      embedded={embedded}
      className={`${className}  ${type === CarouselType.Building ? 'mt-[1.875rem]' : '!pt-[2rem]'}`}
      titleClassname={'pb-20 slg:pb-0 !text-[1.5rem] slg:!text-[2rem] ml-[0.5rem] sm:ml-0 md:ml-[0.5rem]'}
    >
      <div className="relative mt-4">
        {!loading && (
          <div
            className={`absolute flex gap-4 h-fit   ${
              type === CarouselType.Building || type === CarouselType.CustomCard
                ? 'flex right-[0.625rem] -top-[3.75rem] sm:right-0 slg:right-[0.625rem] lg:right-[1rem]'
                : 'flex right-[0.625rem] -top-[3.75rem] sm:right-0 lg:right-[1rem]'
            }  ${buttonPositioning}`}
          >
            <span data-card-carousel-prev-button>
              <AtButton
                ariaLabel="Previous slide"
                className="w-10"
                type={AtButtonTypes.PRIMARY}
                size={AtButtonDimensions.SMALL}
                onClick={() => swiperRef.current?.swiper.slidePrev()}
                IconLeftRef={NavArrowLeft}
                isDisabled={loading || beginning}
              />
            </span>
            <span data-card-carousel-next-button>
              <AtButton
                ariaLabel="Next slide"
                className="w-10"
                type={AtButtonTypes.PRIMARY}
                size={AtButtonDimensions.SMALL}
                onClick={() => swiperRef.current?.swiper.slideNext()}
                IconLeftRef={NavArrowRight}
                isDisabled={loading || end}
              />
            </span>
          </div>
        )}

        <div>
          {isMounted ? (
            <SwiperEngine
              swiperRef={swiperRef}
              type={type}
              cards={cards}
              size={size}
              slideClassName={slideClassName}
              loading={loading}
              loadingPlaceholders={loadingPlaceholders}
              productsSize={productsSize}
              onSwiperReady={onSwiperReady}
              onSlideChange={updateLimits}
              onObserverUpdate={updateLimits}
              onReachEnd={onReachEnd}
            />
          ) : (
            <div className="w-full lg:max-w-[75.5rem] flex gap-2 overflow-hidden">
              {Array.from({ length: loadingPlaceholders }, (_, i) => (
                <div key={i} className={slideClassName} style={{ width: 'max-content' }}>
                  <MlSpaceCardSkeleton size={size} variant={MlSpaceCardSkeletonVariant.CAROUSEL} />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Container>
  )
}

const Container = ({
  children,
  title,
  type,
  embedded,
  className,
  titleClassname,
}: PropsWithChildren<{
  title?: string
  embedded?: boolean
  className?: string
  titleClassname?: string
  type?: CarouselType
}>) => {
  if (embedded) {
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <>{children}</>
  }

  return (
    <section
      className={`${cn(
        `
        ${
          type === CarouselType.Building || type === CarouselType.CustomCard
            ? 'mx-auto w-full  pl-[0.375rem] pr-[0.375rem] pt-8 sm:pl-8 sm:pr-8  md:pl-8 md:pr-8  slg:pl-[2.5rem] slg:pr-[3rem] lg:pl-[9.4375rem] lg:pr-[9.4375rem] lg:pb-8 lg:max-w-screen-lg'
            : 'mx-auto w-full  pl-[0.375rem] pr-[0.375rem] pt-8 sm:pl-[0.875rem] sm:pr-8  md:pl-8 md:pr-8  slg:pl-[2rem] slg:pr-[3rem] lg:pl-[9.4375rem] lg:pr-[9.4375rem] lg:pb-8 lg:max-w-screen-lg '
        }
        `,
        className
      )} `}
    >
      {title && (
        <h2
          data-or-card-carousel-title
          className={
            'font-heading font-medium text-32 leading-[1.3] text-neutrals-900 mb-2 sm:ml-[0.875rem] lg:mb-4 lg:ml-4 ' +
            titleClassname
          }
        >
          {title}
        </h2>
      )}
      {children}
    </section>
  )
}
